// PaymentTermsForm.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Col, DatePicker, Flex, Form, Input, Row, Select, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useWatch } from "antd/es/form/Form";

import { IMasterDataSupplier } from "../../../interfaces/master-data";
import SupplierRule from "../../../components/supplier-rule";
import { ERuleDefaultSettings, ERuleDefaultPaymentTolerances, ERuleDefaultNominalCodes } from "../../../constants/enum";
import { DATE_FORMAT, DATE_FORMAT_YY } from "../../../constants/common";
import DynamicPaymentTable from "../../../components/dynamic-payment-table";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { PERMISSIONS } from "../../../constants/permission";
import { IRequestPaymentTermRule } from "../../../interfaces/supplier";

const createRowData = () => ({
  timing: null,
  eventType: null,
  duration: null,
  calculationBasis: null,
  absoluteDriver: null,
  amount: null,
  index: 0,
});

const defaultFinal = {
  calculationBasis: "Percentage",
  amount: 100,
};

interface PaymentTermsFormProps {
  form: any;
  initialValues: IRequestPaymentTermRule[];
  masterData: IMasterDataSupplier;
}

const applyDefaultRuleFields = (updatedRule: any, ruleType: any[], defaultRule: any, excludeField: string) => {
  ruleType
    .filter((field) => field.name !== excludeField)
    .forEach((field) => {
      updatedRule[field.name] = defaultRule?.[field.name] ?? false;
      if (field.extraInput) {
        updatedRule[field.extraInput] = defaultRule?.[field.extraInput] ?? null;
      }
      if (field.selectInput) {
        updatedRule[field.selectInput] = defaultRule?.[field.selectInput] ?? null;
      }
    });
};

const resetUnsetFields = (updatedRule: any, fields: any[], excludeField: string) => {
  fields.forEach((field) => {
    if (field.name === excludeField) return;
    if (updatedRule[field.name] === false) {
      if (field.extraInput) updatedRule[field.extraInput] = null;
      if (field.selectInput) updatedRule[field.selectInput] = null;
    }
  });
};

const isDateOverlap = (startA: Dayjs, endA: Dayjs, startB: Dayjs, endB: Dayjs) => {
  return !endA.isBefore(startB) && !startA.isAfter(endB);
};

const PaymentTermsForm: React.FC<PaymentTermsFormProps> = ({ form, initialValues, masterData }) => {
  const {
    termTypes = [],
    installments = [],
    eventTypes = [],
    timings = [],
    absoluteDrivers = [],
    calculationBasis = [],
    defaultTermsRule = {},
    accountDebits = [],
    accountCredits = [],
    autosMatch = [],
  } = masterData;

  const hasSupplierEditor = useHasPermission([PERMISSIONS.SUPPLIER_EDITOR]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isCurrentRuleValid, setIsCurrentRuleValid] = useState(true);
  const skipOnChange = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paymentTermsRules = useWatch("paymentTermsRules", form) || [];

  const getDisabledDates = (currentIndex: number, isFrom: boolean) => (currentDate: dayjs.Dayjs) => {
    const currentItem = paymentTermsRules[currentIndex];
    if (!currentItem) return false;

    const currentFrom = currentItem.validFrom ? dayjs(currentItem.validFrom) : null;
    const currentTo = currentItem.validTo ? dayjs(currentItem.validTo) : null;

    if (isFrom && currentTo && !currentDate.isBefore(currentTo)) return true;
    if (!isFrom && currentFrom && !currentDate.isAfter(currentFrom)) return true;

    const selectedFrom = isFrom ? currentDate : currentFrom;
    const selectedTo = isFrom ? currentTo : currentDate;

    for (let i = 0; i < paymentTermsRules.length; i++) {
      if (i === currentIndex) continue;
      const rule = paymentTermsRules[i];
      if (!rule?.validFrom) continue;

      const otherFrom = dayjs(rule.validFrom);
      const otherTo = rule.validTo ? dayjs(rule.validTo) : dayjs("9999-12-31");

      if (isDateOverlap(selectedFrom!, selectedTo ?? dayjs("9999-12-31"), otherFrom, otherTo)) return true;
    }

    return false;
  };

  const onChangeForm = (changedValues: any, allValues: any) => {
    if (skipOnChange.current || !changedValues?.paymentTermsRules) return;

    const index = changedValues.paymentTermsRules.findIndex((item: any) => item !== undefined);
    if (index === -1) return;

    const changedItem = changedValues.paymentTermsRules[index];
    const newList = [...(allValues.paymentTermsRules || [])];
    if (changedItem?.noOfInstallments) {
      const found = installments.find((_, idx) => idx + 1 === changedItem.noOfInstallments);
      const newStageLength = changedItem.noOfInstallments - 2;
      const newStages = Array.from({ length: newStageLength }, (_, idx) => ({
        ...createRowData(),
        index: idx,
      }));

      newList[index] = {
        ...newList[index],
        termName: found?.label || "",
        deposit: changedItem.noOfInstallments >= 2 ? createRowData() : undefined,
        stages: changedItem.noOfInstallments >= 3 ? newStages : undefined,
        final: changedItem.noOfInstallments >= 1 ? { ...createRowData(), ...defaultFinal } : undefined,
      };
      form.setFieldsValue({ paymentTermsRules: newList });
    }

    const updatedRule = { ...newList[index]?.rule };
    if (changedItem?.rule?.isApplyAllDefaultSettings) {
      applyDefaultRuleFields(updatedRule, ERuleDefaultSettings, defaultTermsRule?.rule, "isApplyAllDefaultSettings");
    }
    if (changedItem?.rule?.isApplyDefaultTolerance) {
      applyDefaultRuleFields(
        updatedRule,
        ERuleDefaultPaymentTolerances,
        defaultTermsRule?.rule,
        "isApplyDefaultTolerance"
      );
    }
    if (changedItem?.rule?.isApplyDefaultSageCodes) {
      applyDefaultRuleFields(updatedRule, ERuleDefaultNominalCodes, defaultTermsRule?.rule, "isApplyDefaultSageCodes");
    }

    resetUnsetFields(updatedRule, ERuleDefaultPaymentTolerances, "isApplyDefaultTolerance");
    resetUnsetFields(updatedRule, ERuleDefaultNominalCodes, "isApplyDefaultSageCodes");

    // check render one vaue between {isBspCost, isTourEvent, isOtherSupplier}
    const changedRuleItem = changedValues.paymentTermsRules[index]?.rule || {};
    if (changedRuleItem.isBspCost) {
      updatedRule["isTourEvent"] = false;
      updatedRule["isOtherSupplier"] = false;
    } else if (changedRuleItem.isTourEvent) {
      updatedRule["isBspCost"] = false;
      updatedRule["isOtherSupplier"] = false;
    } else if (changedRuleItem.isOtherSupplier) {
      updatedRule["isBspCost"] = false;
      updatedRule["isTourEvent"] = false;
    }

    newList[index].rule = updatedRule;
    form.setFieldsValue({ paymentTermsRules: newList });
  };

  const formatData = (dataForm: any) => {
    if (dataForm) {
      const converted = dataForm.map((item: any) => ({
        ...item,
        validFrom: item.validFrom ? dayjs(item.validFrom) : null,
        validTo: item.validTo ? dayjs(item.validTo) : null,
      }));
      return converted;
    }
  };

  useEffect(() => {
    const validateSelectedRule = async () => {
      try {
        await form.validateFields();
        setIsCurrentRuleValid(true);
      } catch {
        setIsCurrentRuleValid(false);
      }
    };
    setTimeout(() => {
      validateSelectedRule();
    }, 10);
  }, [selectedIndex, paymentTermsRules, form]);

  const isValidDate = (date: any) => dayjs(date).isValid();

  return (
    <Form
      form={form}
      name="paymentTermsForm"
      layout="vertical"
      initialValues={{ paymentTermsRules: formatData(initialValues) || [{}] }}
      onValuesChange={onChangeForm}
      disabled={!hasSupplierEditor}
    >
      <Form.List name="paymentTermsRules">
        {(fields, { add, remove }) => {
          const handleAdd = () => {
            skipOnChange.current = true;
            add({...defaultTermsRule, final: {...defaultTermsRule.final,...defaultFinal}}, 0);
            setSelectedIndex(0);
            setTimeout(() => {
              skipOnChange.current = false;
            }, 0);
          };

          const handleClone = (index: number) => {
            skipOnChange.current = true;
            const current = paymentTermsRules?.[index];
            if (!current?.validTo) return;

            const nextValidFrom = dayjs(current.validTo).add(1, "day");
            const cloned = { ...current, validFrom: nextValidFrom, validTo: null, id: undefined };
            add(cloned, 0);
            setSelectedIndex(0);
            setTimeout(() => {
              skipOnChange.current = false;
            }, 0);
          };

          const handleRemove = (index: number) => {
            if (fields.length <= 1) return;
            skipOnChange.current = true;
            remove(index);
            setSelectedIndex((prev) => (prev === index ? 0 : prev > index ? prev - 1 : prev));
            setTimeout(() => {
              skipOnChange.current = false;
            }, 0);
          };

          return (
            <Flex gap={24}>
              <div className="payments-box">
                {hasSupplierEditor && (
                  <Button
                    color="primary"
                    variant="outlined"
                    block
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    style={{ marginTop: 8 }}
                    disabled={!isCurrentRuleValid || !isValidDate(paymentTermsRules?.[0]?.validTo)}
                  >
                    Add New
                  </Button>
                )}

                {fields.map((field, index) => {
                  const term = paymentTermsRules?.[index] || {};
                  const from = term?.validFrom ? dayjs(term.validFrom).format(DATE_FORMAT_YY) : "Start";
                  const to = term?.validTo
                    ? dayjs(term.validTo).format(DATE_FORMAT_YY)
                    : from === "Start"
                    ? "End"
                    : "Ongoing";

                  return (
                    <Flex
                      key={field.key}
                      className={selectedIndex === index ? "active item" : "item"}
                      justify="space-between"
                      align="center"
                      onClick={() => {
                        skipOnChange.current = true;
                        setSelectedIndex(index);
                        setTimeout(() => {
                          skipOnChange.current = false;
                        }, 0);
                      }}
                    >
                      <span>
                        {from} - {to}
                      </span>
                      {hasSupplierEditor && (
                        <Space size="small" className="box-action">
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClone(index);
                            }}
                            disabled={!isValidDate(term?.validTo) || !isCurrentRuleValid || index !== 0}
                            type="link"
                          >
                            <FontAwesomeIcon icon={faClone} />
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(index);
                            }}
                            disabled={fields.length === 1 || !!term?.id}
                            type="link"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </Space>
                      )}
                    </Flex>
                  );
                })}
              </div>

              {fields
                .filter((field) => field.name === selectedIndex)
                .map(({ key, name, ...restField }) => {
                  const term = paymentTermsRules?.[name] || {};
                  const noOfInstallments = Number(term?.noOfInstallments || 0);
                  return (
                    <Card key={key} className="payment-item">
                      <Row gutter={24}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "validFrom"]}
                            label="Valid From"
                            rules={[{ required: true, message: "Complete this required field." }]}
                            layout="horizontal"
                          >
                            <DatePicker format={DATE_FORMAT} disabledDate={getDisabledDates(name, true)} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, "validTo"]} label="to" layout="horizontal">
                            <DatePicker format={DATE_FORMAT} disabledDate={getDisabledDates(name, false)} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <h3>Payment Terms</h3>
                      <Row gutter={24}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "termType"]}
                            label="Term Type"
                            rules={[{ required: true, message: "Complete this required field." }]}
                          >
                            <Select
                              placeholder="Select"
                              options={termTypes.map((item) => ({ value: item.code, label: item.label }))}
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "noOfInstallments"]}
                            label="Number of Installments"
                            rules={[{ required: true, message: "Complete this required field." }]}
                          >
                            <Select
                              placeholder="Select"
                              options={installments.map((_, index) => ({
                                value: index + 1,
                                label: (index + 1).toString(),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, "termName"]} label="Term Structure">
                            <Input disabled />
                          </Form.Item>
                        </Col>
                      </Row>

                      {noOfInstallments >= 2 && (
                        <>
                          <h4>Deposit</h4>
                          <DynamicPaymentTable
                            {...{
                              name,
                              fieldKey: "deposit",
                              eventTypes,
                              timings,
                              absoluteDrivers,
                              calculationBasis,
                              values: term.deposit,
                            }}
                          />
                        </>
                      )}

                      {noOfInstallments >= 3 && (
                        <>
                          <h4>Stage</h4>
                          <DynamicPaymentTable
                            {...{
                              name,
                              fieldKey: "stages",
                              rowCount: noOfInstallments - 2,
                              useRowIndex: true,
                              eventTypes,
                              timings,
                              absoluteDrivers,
                              calculationBasis,
                              values: term.stages,
                            }}
                          />
                        </>
                      )}

                      {noOfInstallments >= 1 && (
                        <>
                          <h4>Final</h4>
                          <DynamicPaymentTable
                            {...{
                              name,
                              fieldKey: "final",
                              eventTypes,
                              timings,
                              absoluteDrivers,
                              calculationBasis,
                              values: term.final,
                            }}
                          />
                        </>
                      )}

                      <SupplierRule
                        {...{ name, accountDebits, accountCredits, autosMatch, isEdit: hasSupplierEditor }}
                      />
                    </Card>
                  );
                })}
            </Flex>
          );
        }}
      </Form.List>
    </Form>
  );
};

export default PaymentTermsForm;
